import { useApolloClient } from '@apollo/client';
import { useCallback } from 'react';
import { useStore } from 'react-redux';
import { useStudioProjectContext } from 'pages/dashboard/studio/StudioProjectProvider';
import { AppState } from 'store';
import { useEffectiveStudioLimit } from '../../Sections/hooks/useProjectSections';
import useSectionViewOptions from '../../Sections/hooks/useSectionViewOptions';
import {
  SectionsDocument,
  SectionsQuery,
  SectionsQueryVariables,
} from '../../Sections/operation.generated';
import {
  resolveSectionFormattedText,
  SectionFormattedText,
} from './useSectionsFormattedText';

/*
 * Returns a stable getter for formatted text by section id, read imperatively
 * from the Apollo cache and the store at call time rather than subscribed
 * to. Use this instead of useSectionsFormattedText when the result is only
 * needed inside an event handler (not rendered) - it won't re-render on
 * section or draft changes.
 */
const useGetSectionsFormattedText = () => {
  const { projectId } = useStudioProjectContext();
  const client = useApolloClient();
  const store = useStore<AppState>();
  const effectiveLimit = useEffectiveStudioLimit();
  const sectionViewOptions = useSectionViewOptions();

  return useCallback(
    (ids: string[]): SectionFormattedText[] => {
      const draftsById = store.getState().scriptStudioBuffers.byId;
      const cached = client.readQuery<SectionsQuery, SectionsQueryVariables>({
        query: SectionsDocument,
        variables: { inputs: { projectId, limit: effectiveLimit } },
      });
      const sections = cached?.sections.edges.map(edge => edge.node) ?? [];
      const idSet = new Set(ids);
      return sections
        .map(section =>
          section && idSet.has(section.id)
            ? resolveSectionFormattedText(
                section,
                draftsById[section.id],
                sectionViewOptions
              )
            : null
        )
        .filter((section): section is SectionFormattedText => section !== null);
    },
    [client, projectId, store, effectiveLimit, sectionViewOptions]
  );
};

export default useGetSectionsFormattedText;
